import { BillingSummary, ColumnConfig, EmployeeBillingSummary, SummaryFieldConfig } from './billing-dashboard.component';

export function exportBillingDashboardToPDF(
  billingSummary: BillingSummary,
  employeeSummaries: EmployeeBillingSummary[],
  currentCycleStart: string,
  currentCycleEnd: string,
  excludeDanGuud: boolean,
  visibleColumns: ColumnConfig[],
  summaryFields?: SummaryFieldConfig[],
  entityLabel?: string
): void {
  const entity = entityLabel || 'Employee';
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    return;
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  // Fallback: if no selection is provided, show all summary fields.
  const selectedSummaryFields: SummaryFieldConfig[] = (summaryFields && summaryFields.length > 0)
    ? summaryFields
    : [
        { key: 'totalCustomers', label: 'Total Customers', type: 'count', visible: true },
        { key: 'totalBilledCustomers', label: 'Billed Customers', type: 'count', visible: true },
        { key: 'totalEmployees', label: 'Total Employees', type: 'count', visible: true },
        { key: 'totalUnitsConsumed', label: 'Total Units Consumed', type: 'count', visible: true },
        { key: 'totalBillAmount', label: 'Grand Total Bill', type: 'currency', visible: true },
        { key: 'totalPaid', label: 'Total Paid', type: 'currency', visible: true },
        { key: 'totalRh', label: 'Total RH', type: 'currency', visible: true },
        { key: 'totalRc', label: 'Total RC', type: 'currency', visible: true },
        { key: 'totalBadDebt', label: 'Total Daan Guud', type: 'currency', visible: true },
        { key: 'totalDiscount', label: 'Total Discount', type: 'currency', visible: true },
        { key: 'totalVat', label: 'Total VAT', type: 'currency', visible: true },
        { key: 'totalWadarta', label: 'Total Wadarta', type: 'currency', visible: true },
        { key: 'totalBankAmount', label: 'Total Bank Amount', type: 'currency', visible: true },
        { key: 'totalFarqi', label: 'Total Farqi', type: 'currency', visible: true },
        { key: 'totalMerchantDebit', label: 'Merchant Debit', type: 'currency', visible: true },
        { key: 'totalMerchantCredit', label: 'Merchant Credit', type: 'currency', visible: true },
        { key: 'totalAccountSent', label: 'Account Sent', type: 'currency', visible: true },
        { key: 'totalAccountReceived', label: 'Account Received', type: 'currency', visible: true }
      ];

  const summaryFieldValue = (field: SummaryFieldConfig): string => {
    if (field.key === 'totalEmployees') {
      return employeeSummaries.length.toString();
    }
    const raw = (billingSummary as any)[field.key];
    return field.type === 'count'
      ? (raw ?? 0).toString()
      : formatCurrency(raw as number);
  };

  const summaryItems = selectedSummaryFields.map(field => ({
    label: field.label,
    value: summaryFieldValue(field)
  }));

  const summaryCards = summaryItems.map(item =>
    `<div class='summary-card'>` +
    `<div class='label'>${item.label}</div>` +
    `<div class='value'>${item.value}</div>` +
    `</div>`
  ).join('');

  const isCountColumn = (key: string) =>
    key === 'employeeId' || key === 'employeeName' || key === 'totalCustomers' ||
    key === 'totalBilledCustomers' || key === 'totalUnitsConsumed';

  const isLabelColumn = (key: string) => key === 'employeeId' || key === 'employeeName';

  const formatValue = (value: any, key: string) => {
    if (isCountColumn(key)) {
      return value?.toString() || '0';
    }
    return formatCurrency(value as number);
  };

  const headerAlign = (key: string) => isLabelColumn(key) ? 'left' : 'right';

  const headerRow = '<tr>' + visibleColumns.map(col =>
    `<th style='text-align:${headerAlign(col.key)}'>${col.label}</th>`
  ).join('') + '</tr>';

  const rows = employeeSummaries.map(emp => {
    const cells = visibleColumns.map(col => {
      const value = emp[col.key];
      const align = isLabelColumn(col.key) ? 'left' : 'right';
      return `<td style='text-align:${align}'>${formatValue(value, col.key)}</td>`;
    }).join('');
    return `<tr>${cells}</tr>`;
  }).join('');

  const labelCols = visibleColumns.filter(c => isLabelColumn(c.key));
  const labelSpan = labelCols.length;
  const totalCells = visibleColumns
    .filter(c => !isLabelColumn(c.key))
    .map(col => {
      const value = (billingSummary as any)[col.key];
      const align = isLabelColumn(col.key) ? 'left' : 'right';
      return `<td style='text-align:${align}'><strong>${formatValue(value, col.key)}</strong></td>`;
    })
    .join('');

  const totalRow =
    `<tr class='total-row'>` +
    (labelSpan > 0 ? `<td colspan='${labelSpan}' style='text-align:left'><strong>Total</strong></td>` : '') +
    totalCells +
    `</tr>`;

  const html = `<!DOCTYPE html>
<html>
<head>
  <title>Billing Dashboard Executive Report</title>
  <style>
    @page { size: landscape; margin: 12mm; }
    body { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; margin: 0; padding: 24px; color: #1a1a2e; background: #fff; }
    .header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #0f766e; padding-bottom: 14px; margin-bottom: 24px; }
    .header h1 { color: #0f766e; margin: 0; font-size: 26px; letter-spacing: 0.02em; }
    .header .subtitle { font-size: 13px; color: #64748b; margin-top: 6px; }
    .header .meta { text-align: right; font-size: 12px; color: #475569; line-height: 1.6; }
    .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 14px; margin-bottom: 28px; }
    .summary-card { border-radius: 10px; padding: 14px 16px; background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%); border: 1px solid #99f6e4; box-shadow: 0 2px 6px rgba(15, 118, 110, 0.08); }
    .summary-card .label { font-size: 10px; text-transform: uppercase; color: #0f766e; margin-bottom: 8px; letter-spacing: 0.06em; font-weight: 600; }
    .summary-card .value { font-size: 17px; font-weight: 700; color: #111827; }
    h2 { color: #0f766e; font-size: 17px; margin-bottom: 12px; font-weight: 600; }
    table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 9.5px; margin-top: 10px; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
    th, td { border-bottom: 1px solid #e2e8f0; padding: 7px 8px; }
    th { background: #0f766e; color: #fff; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
    td { background: #fff; color: #1e293b; }
    tr:nth-child(even) td { background: #f8fafc; }
    tr:hover td { background: #f0fdfa; }
    .total-row td { background: #e0f2fe !important; color: #0f172a; font-weight: 700; border-top: 2px solid #0f766e; }
    .footer { margin-top: 28px; font-size: 10px; color: #94a3b8; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 12px; }
  </style>
</head>
<body>
  <div class='header'>
    <div>
      <h1>Billing Dashboard Executive Report</h1>
      <div class='subtitle'>Xisaab Xerka — SomWater</div>
    </div>
    <div class='meta'>
      <div><strong>Period:</strong> ${currentCycleStart} — ${currentCycleEnd}</div>
      <div><strong>Generated:</strong> ${new Date().toLocaleString()}</div>
      <div><strong>Filter:</strong> ${excludeDanGuud ? 'Non-Dan Guud' : 'All Customers'}</div>
    </div>
  </div>
  <div class='summary-grid'>
    ${summaryCards}
  </div>
  <h2>${entity} Performance</h2>
  <table>
    <thead>${headerRow}</thead>
    <tbody>${rows}${totalRow}</tbody>
  </table>
  <div class='footer'>SomWater Billing Dashboard — Confidential | For Senior Management Review</div>
</body>
</html>`;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => printWindow.print(), 150);
}
